<template>
  <div
    ref="root"
    class="io-card p-7.5"
    :class="name && getLink ? 'cursor-pointer' : ''"
    @click.stop="clickHandler"
    @keydown.enter="clickHandler"
  >
    <div
      v-if="name"
      class="io-card-header"
      :class="hasContent ? 'mb-7.5' : ''"
    >
      <div
        v-if="getIcon"
        class="io-card-header-icon"
        :style="{ ...iconBackground }"
      >
        <BaseIcon
          v-if="!iconBackground"
          :icon="getIcon"
          :style="{
            fontSize: '1.5rem',
            color: 'var(--color-white)',
          }"
        />
      </div>
      <div class="io-card-header-content">
        <p
          v-if="getTitle"
          class="io-card-title"
        >
          <span>
            {{ getTitle }}
          </span>
          <small v-if="getTopInfo">
            {{ getTopInfo }}
          </small>
        </p>
        <p
          v-if="getSubtitle"
          class="io-card-subtitle"
        >
          {{ getSubtitle }}
        </p>
      </div>
      <BaseContextButton
        v-if="isSlot(name) || actions"
        :actions="actions"
        @action="toggleModal($event)"
      />
      <slot
        v-if="action"
        name="action"
        :action="action"
        :toggle-modal="toggleModal"
      />
      <BaseModal
        v-if="!defaultModal"
        v-show="modalOpen"
        v-bind="modalProps"
        :title="modalTitle"
        :body-scroll="false"
        @close="toggleModal()"
      >
        <template
          v-if="isSlot(name)"
          #content
        >
          <slot name="actions" />
        </template>
        <template
          v-else-if="(actions || action) && currentAction"
          #content
        >
          <component
            :is="component"
            v-bind="componentProps"
            @close="toggleModal()"
            @success="success($event)"
          />
        </template>
      </BaseModal>
    </div>
    <div v-if="hasContent">
      <slot v-if="hasSlotDefault" />
      <template v-else>
        <p class="io-card-value">
          {{ vars.info }}
        </p>
        <p
          v-if="vars.subinfo"
          class="io-card-measure"
        >
          {{ vars.subinfo }}
        </p>
      </template>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import axios from 'axios';

export default {
  name: 'BaseIconCard',
  props: {
    name: {
      type: String,
      required: true,
    },
    endpoint: {
      type: [String, Array],
      required: false,
    },
    actions: {
      type: Array,
      required: false,
    },
    action: {
      type: Object,
      required: false,
    },
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    link: {
      type: [String, Object],
      default: '',
    },
    icon: {
      type: String,
      required: false,
    },
    modalProps: {
      type: Object,
      required: false,
    },
    value: {
      type: [String, Number],
    },
    topInfo: {
      type: String,
      required: false,
    },
    subinfo: {
      type: String,
      required: false,
    },
    data: {
      type: [Array, Object],
      default: () => [],
      required: false,
    },
    defaultModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      vars: {},
      currentTry: 0,
      iconBackground: null,
      loading: false,
      modalOpen: false,
      currentAction: null,
      modalComponentProps: {},
    };
  },
  computed: {
    hasContent() {
      return this.vars.info || this.vars.subinfo || this.hasSlotDefault;
    },
    hasSlotDefault() {
      return this.$slots.default;
    },
    component() {
      return this.currentAction?.data?.component || this.currentAction?.component;
    },
    componentProps() {
      const props = this.currentAction?.data || this.currentAction;
      return {
        ...props,
        ...this.modalComponentProps,
        reference: this,
      };
    },
    modalTitle() {
      return this.currentAction?.data?.title || this.currentAction?.title || this.getTitle;
    },
    getTitle() {
      return this.vars.title || this.title;
    },
    getTopInfo() {
      return this.vars.topInfo || this.topInfo;
    },
    getSubtitle() {
      return this.vars.subtitle || this.subtitle;
    },
    getIcon() {
      const icon = this.vars.icon || this.icon || '';
      if (icon.includes('custom:')) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.iconBackground = {
          backgroundImage: `url("img/icons/${icon.replace('custom:', '')}.svg")`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        };
      }
      return icon;
    },
    getLink() {
      return this.vars.link || this.vars.url || this.link;
    },
  },
  watch: {
    vars: {
      handler(vars) {
        this.$emit('update:vars', vars);
        this.loading = false;
      },
      deep: true,
    },
  },
  mounted() {
    this.loading = true;
    if (this.value || !Number.isNaN(this.value)) {
      this.vars = { ...this.vars, info: this.value };
      this.$emit('update:vars', this.vars);
    }

    if (this.subinfo) {
      this.vars = { ...this.vars, subinfo: this.subinfo };
      this.$emit('update:vars', this.vars);
    }

    if (this.data) {
      this.vars = { ...this.vars, data: this.data };
      this.$emit('update:vars', this.vars);
    }

    if (this.endpoint && Array.isArray(this.endpoint)) {
      const [url, method, data] = this.endpoint;

      axios[method](url, data).then((response) => {
        this.vars = response.data;
      });
    } else if (this.endpoint) {
      axios.get(this.endpoint).then((response) => {
        this.vars = response.data;
      });
    }
    this.setVars();
  },
  methods: {
    ...mapMutations('baseModal', {
      SET_PROPS: 'SET_PROPS',
      SET_TITLE: 'SET_TITLE',
      SET_COMPONENT: 'SET_COMPONENT',
    }),
    clickHandler(event) {
      if (typeof this.getLink === 'string') {
        this.redirect(event);
      } else if (this.getLink) {
        this.toggleModal(this.getLink);
      }
    },
    toggleModal(val) {
      if (this.defaultModal) {
        if (val.type === 'modal') {
          this.SET_PROPS({
            ...val.data,
            ...this.modalComponentProps,
            reference: this,
          });
          this.SET_TITLE(val.data.title || this.modalTitle);
          this.SET_COMPONENT(val.data.component);
        } else {
          this.SET_PROPS({
            ...val,
            ...this.modalComponentProps,
            reference: this,
          });
          this.SET_TITLE(val.title);
          this.SET_COMPONENT(val.component);
        }
      } else {
        this.currentAction = val;
        this.modalOpen = !this.modalOpen;
      }
    },
    setVars() {
      if (this.currentTry <= 10) {
        this.currentTry += 1;
        setTimeout(this.setVars, 100);
      }
    },
    isSlot(name) {
      return this.$slots[`${name}_actions`] !== undefined;
    },
    redirect(e) {
      const isRoot = this.$refs.root === e.target;
      const otherEventSet = !!e.target.onClick;
      const isLinkOrButton = ['a', 'button'].includes((e.target.tagName || '').toLowerCase());
      const preventRedirect = isRoot ? false : (otherEventSet || isLinkOrButton);

      if (this.name && this.getLink && !preventRedirect) {
        window.location.href = this.getLink;
      }
    },
    success({ data, ...rest }) {
      this.modalComponentProps = rest;
      const vars = { ...this.vars };
      this.vars = {};
      this.vars = {
        ...vars,
        ...data,
      };
      this.modalOpen = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.card-body {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.io-card {
    @apply bg-white
        rounded-2xl
        shadow-card;

    &-header {
        @apply flex;

        align-items: center;
        max-height: 3.125rem;

        &-icon {
          @apply flex
            items-center
            justify-center
            rounded-full
            mr-5;
            font-size: 3.125rem;
            min-width: 1em;
            min-height: 1em;
            background-color: #20535c;
            background-color: var(--company-main-primary);
        }

        &-content {
            display: flex;
            flex-direction: column;
            justify-content: center;
            flex: 1;
        }
    }

    &-title {
        @apply text-gray2;

        font-size: 1.25rem;
        font-weight: 700;

        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: nowrap;

        span, small {
            @apply text-gray2;

            font-weight: 700;
        }

        span {
            font-size: 1.25rem;
        }

        small {
            font-size: 1rem;
            white-space: nowrap;
        }
    }

    &-subtitle {
        color: #CCCCCC;
    }

    &-value {
        color: var(--company-main-primary);
        @apply mb-4
            font-bold;

        font-size: 2.5rem;
    }

    &-measure {
        color: #CCCCCC;
    }

    &:not(div):hover {
        text-decoration: none;
    }
}
</style>
